import { z } from "zod";

/**
 * Form validation schemas using Zod
 * These schemas are used with React Hook Form for client-side validation
 */

// Contact Modal form schema
export const ContactModalSchema = z.object({
  email: z
    .string()
    .min(1, "Please enter your email address")
    .email("Please enter a valid email address")
    .max(254, "Email address is too long"),
  customRequest: z
    .string()
    .max(2000, "Custom request is too long")
    .optional()
    .or(z.literal("")),
});

export type ContactModalFormData = z.infer<typeof ContactModalSchema>;

// Notify Modal form schema
export const NotifyModalSchema = z.object({
  email: z
    .string()
    .min(1, "Please enter your email address")
    .email("Please enter a valid email address")
    .max(254, "Email address is too long"),
});

export type NotifyModalFormData = z.infer<typeof NotifyModalSchema>;

// Other Input Modal form schema
export const OtherInputModalSchema = z.object({
  value: z
    .string()
    .min(1, "Please enter a value")
    .max(500, "Value is too long"),
});

export type OtherInputModalFormData = z.infer<typeof OtherInputModalSchema>;

// Feedback Widget Phase 2 form schema (for detailed feedback modal)
export const FeedbackPhase2FormSchema = z.object({
  whatWorked: z.array(z.string()).optional(),
  whatDidntWork: z.array(z.string()).optional(),
  additionalComments: z
    .string()
    .max(1000, "Comments are too long")
    .optional()
    .or(z.literal("")),
});

export type FeedbackPhase2FormData = z.infer<typeof FeedbackPhase2FormSchema>;

// Feedback Modal form schema (for new product feedback)
export const FeedbackModalFormSchema = z.object({
  additionalComments: z
    .string()
    .max(1000, "Comments are too long")
    .optional()
    .or(z.literal("")),
});

export type FeedbackModalFormData = z.infer<typeof FeedbackModalFormSchema>;

// Admin form schemas

// Edit Recommendation form schema (for editing existing recommendations)
export const EditRecommendationFormSchema = z.object({
  id: z.string().min(1, "ID is required"),
  name: z.string().min(1, "Name is required").max(200, "Name is too long"),
  type: z.enum(
    [
      "documentation",
      "course",
      "contact",
      "notify",
      "other",
      "article",
      "video",
    ],
    {
      errorMap: () => ({ message: "Please select a valid type" }),
    },
  ),
  iconKey: z
    .string()
    .min(1, "Icon key is required")
    .max(50, "Icon key is too long"),
  iconColor: z
    .string()
    .min(1, "Icon color is required")
    .max(20, "Icon color is too long"),
  shortDescription: z
    .string()
    .min(1, "Short description is required")
    .max(500, "Short description is too long"),
  description: z
    .string()
    .max(2000, "Description is too long")
    .optional()
    .or(z.literal("")),
  durationInMinutes: z
    .number()
    .min(0, "Duration must be positive")
    .max(1440, "Duration cannot exceed 24 hours")
    .optional(),
  benefits: z.array(z.string()).optional(),
  // New simplified primary action
  primaryAction: z
    .object({
      url: z
        .string()
        .url("Please enter a valid URL")
        .max(500, "URL is too long")
        .optional()
        .or(z.literal("")),
      ctaLabel: z
        .string()
        .max(50, "CTA label is too long")
        .optional()
        .or(z.literal("")),
    })
    .optional(),
});

export type EditRecommendationFormData = z.infer<
  typeof EditRecommendationFormSchema
>;

// New Recommendation form schema (for creating new recommendations)
export const NewRecommendationFormSchema = z.object({
  type: z.enum(
    [
      "documentation",
      "course",
      "contact",
      "notify",
      "other",
      "article",
      "video",
    ],
    {
      errorMap: () => ({ message: "Please select a valid type" }),
    },
  ),
  name: z.string().min(1, "Name is required").max(200, "Name is too long"),
  iconKey: z
    .string()
    .min(1, "Icon key is required")
    .max(50, "Icon key is too long"),
  iconColor: z
    .string()
    .min(1, "Icon color is required")
    .max(20, "Icon color is too long"),
  shortDescription: z
    .string()
    .min(1, "Short description is required")
    .max(500, "Short description is too long"),
  description: z
    .string()
    .max(2000, "Description is too long")
    .optional()
    .or(z.literal("")),
  durationInMinutes: z
    .number()
    .min(0, "Duration must be positive")
    .max(1440, "Duration cannot exceed 24 hours"),
  benefits: z
    .array(z.string())
    .min(3, "At least 3 benefits are required")
    .max(5, "Maximum 5 benefits allowed"),
  // New simplified primary action
  primaryAction: z
    .object({
      url: z
        .string()
        .url("Please enter a valid URL")
        .max(500, "URL is too long")
        .optional()
        .or(z.literal("")),
      ctaLabel: z
        .string()
        .max(50, "CTA label is too long")
        .optional()
        .or(z.literal("")),
    })
    .optional(),
});

export type NewRecommendationFormData = z.infer<
  typeof NewRecommendationFormSchema
>;

// Edit Question form schema - uses same validation rules as the main schema
export const EditQuestionFormSchema = z.object({
  questionName: z
    .string()
    .min(1, "Question name is required")
    .max(200, "Question name is too long"),
  questionText: z.string().min(1, "Question text is required"),
  questionDescription: z.string().optional().or(z.literal("")),
  skippable: z.boolean(),
  multiSelect: z.boolean(),
  nextTopicQuestionId: z.string().optional().or(z.literal("")),
  options: z.array(
    z.object({
      value: z.string().min(1, "Option value is required"),
      label: z.string().min(1, "Option label is required"),
      tooltip: z.string().optional().or(z.literal("")),
      deeperQuestionId: z.string().optional().or(z.literal("")),
      bootstrapIconName: z.string().optional().or(z.literal("")),
      simpleIconName: z.string().optional().or(z.literal("")),
      customImage: z.string().optional().or(z.literal("")),
      color: z.string().optional().or(z.literal("")),
    }),
  ),
});

export type EditQuestionFormData = z.infer<typeof EditQuestionFormSchema>;
